import {
  localDayKey,
  STATUS_DE_MULTA,
  type StatusDeMulta,
  TAMANHO_MAXIMO_DE_PAGINA,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { type FiltrosDeVeiculos, type PaginaDeVeiculos, useVeiculos } from '@/features/frota/api'
import { paginaDe } from '@/features/precos/api'
import { apiGet, apiPatch, apiPost } from '@/lib/api'
import { ROTAS_DE_MULTAS, ROTAS_DE_RESERVAS } from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

/**
 * Multas de trânsito — Gap 4 (Fase 4).
 *
 * ---------------------------------------------------------------------------
 * A VINCULAÇÃO É DA API, NUNCA DA TELA
 * ---------------------------------------------------------------------------
 * O registro envia placa (via veículo) + data da infração, e é o SERVIÇO que
 * cruza isso com as alocações para achar a reserva. Não existe campo de
 * reserva no formulário de propósito: digitar a reserva é como a multa do
 * cliente A vai parar na fatura do B. `reservationId` só aparece na LEITURA.
 *
 * ---------------------------------------------------------------------------
 * O PRAZO DE INDICAÇÃO É A COLUNA MAIS IMPORTANTE DA TELA
 * ---------------------------------------------------------------------------
 * Perder o prazo legal de indicação do condutor dobra a multa para a locadora
 * (a multa NIC — não indicação de condutor). A listagem é, antes de tudo, uma
 * fila de urgência: o filtro `prazoAte` e o alerta de "vence em N dias" servem
 * a essa fila, e o limiar de alerta mora aqui para a página e o diálogo
 * dizerem o mesmo número.
 *
 * ---------------------------------------------------------------------------
 * O QUE VEM DE ONDE
 * ---------------------------------------------------------------------------
 * O modelo de LEITURA espelha `multas.dto.ts` da API (Zod local, como nas
 * demais features). Os contratos de ENTRADA (`registrarMultaSchema`,
 * `indicarCondutorSchema`) vêm de `@locaveri/shared` e são usados no diálogo.
 * Dinheiro chega como STRING de centavos (invariante nº 1) e é formatado por
 * `lib/dinheiro`; `prazoParaIndicacao` é data de CALENDÁRIO (`AAAA-MM-DD`) e
 * nunca passa por `new Date(...)` direto.
 */

// ---------------------------------------------------------------------------
// Modelo de leitura
// ---------------------------------------------------------------------------

/**
 * A multa como a API a devolve. `veiculoPlaca` e `condutorNome` viajam junto
 * porque quem lê a lista decide uma URGÊNCIA — resolver ids em chamadas extras
 * é como a fila deixa de ser lida. `reservationId` nulo conta a história: ou a
 * multa é da operação (pátio, translado), ou espera conciliação manual.
 */
export const multaSchema = z.object({
  id: z.uuid(),
  vehicleId: z.uuid(),
  veiculoPlaca: z.string(),
  /** `null` quando nenhuma alocação cobria a data da infração. */
  reservationId: z.uuid().nullable(),
  /** O condutor DA reserva indicado ao órgão. `null` enquanto não indicado. */
  driverId: z.uuid().nullable(),
  condutorNome: z.string().nullable(),

  dataDaInfracao: z.string(),
  autoDeInfracao: z.string(),
  orgaoAutuador: z.string(),
  descricao: z.string(),

  /** Centavos como string decimal (o wire de `Money`). */
  valor: z.string(),
  pontos: z.number().int(),

  /** Prazo LEGAL da indicação, `AAAA-MM-DD`. Perdê-lo dobra a multa (NIC). */
  prazoParaIndicacao: z.string(),

  status: z.enum(STATUS_DE_MULTA),

  condutorIndicadoEm: z.string().nullable(),
  cobradaEm: z.string().nullable(),

  /** Multa pós-devolução: a taxa aplicada, por onde saiu, a data agendada, o erro. */
  taxaAdministrativa: z.string().nullable(),
  cobradaVia: z.enum(['FECHAMENTO', 'CAUCAO', 'CARTAO', 'MANUAL']).nullable(),
  cobrancaAgendadaPara: z.string().nullable(),
  erroDaCobranca: z.string().nullable(),

  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Multa = z.infer<typeof multaSchema>

export const paginaDeMultasSchema = paginaDe(multaSchema)

export type PaginaDeMultas = z.infer<typeof paginaDeMultasSchema>

/**
 * Os condutores da reserva vinculada, lidos do DETALHE da reserva — o Zod
 * descarta o resto da resposta (timeline, fechamento, snapshot): este módulo
 * só precisa de quem pode ser indicado, e espelhar o detalhe inteiro aqui
 * duplicaria um contrato que pertence à feature de reservas.
 */
export const condutorDaReservaSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  cpf: z.string(),
  principal: z.boolean(),
})

export type CondutorDaReserva = z.infer<typeof condutorDaReservaSchema>

export const reservaComCondutoresSchema = z.object({
  id: z.uuid(),
  numero: z.number().int(),
  condutores: z.array(condutorDaReservaSchema),
})

export type ReservaComCondutores = z.infer<typeof reservaComCondutoresSchema>

/**
 * Corpo já validado por um schema de `@locaveri/shared`, pronto para o fio.
 * Mapa aberto pelo mesmo motivo de `CorpoDeExtra`: `moneySchemaPositive`
 * transforma o valor em `bigint` e `instanteSchema` em `Date` — o que viaja é
 * a ENTRADA validada, nunca `validado.data`.
 */
export type CorpoDeMulta = Readonly<Record<string, unknown>>

// ---------------------------------------------------------------------------
// Status que um humano grava
// ---------------------------------------------------------------------------

/**
 * Dos seis status, só TRÊS são gesto humano — os outros três são escritos pelo
 * sistema (`RECEBIDA`/`VINCULADA` no registro, `CONDUTOR_INDICADO` pela
 * indicação). O `satisfies` sobre a união do shared faz um status renomeado lá
 * quebrar aqui em compilação, o mesmo pacto de `multas.dto.ts` na API.
 */
export const STATUS_DE_MULTA_ATUALIZAVEIS = [
  'COBRADA',
  'CONTESTADA',
  'CANCELADA',
] as const satisfies readonly StatusDeMulta[]

export type StatusDeMultaAtualizavel = (typeof STATUS_DE_MULTA_ATUALIZAVEIS)[number]

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

export interface FiltrosDeMultas {
  readonly pagina: number
  readonly tamanho: number
  readonly status: StatusDeMulta | ''
  /** Multas cujo prazo de indicação vence até esta data — a lista de urgência. */
  readonly prazoAte: string
}

// ---------------------------------------------------------------------------
// Chaves de cache
// ---------------------------------------------------------------------------

export const CHAVE_DE_MULTAS = 'multas'

/**
 * Chave própria, e não a de reservas: a feature de reservas tem as chaves
 * dela, e este módulo só lê um recorte (os condutores). Invalidar a lista de
 * multas não precisa arrastar o detalhe da reserva, e vice-versa.
 */
export const CHAVE_DE_CONDUTORES_DA_RESERVA = 'condutores-da-reserva'

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

export function useMultas(filtros: FiltrosDeMultas): UseQueryResult<PaginaDeMultas> {
  return useQuery({
    queryKey: [CHAVE_DE_MULTAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        status: filtros.status,
        prazoAte: filtros.prazoAte,
      })

      return apiGet(`${ROTAS_DE_MULTAS.lista}${query}`, paginaDeMultasSchema, { signal })
    },
    // Mantém a página anterior enquanto a nova carrega: sem isso a tabela
    // pisca em branco a cada mudança de filtro.
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Os condutores da reserva vinculada à multa, para o diálogo de indicação.
 * Desligada sem reserva: multa não vinculada não tem quem indicar, e a API
 * responderia 404 para um id vazio.
 */
export function useCondutoresDaReserva(
  reservationId: string | null,
): UseQueryResult<ReservaComCondutores> {
  return useQuery({
    queryKey: [CHAVE_DE_CONDUTORES_DA_RESERVA, reservationId],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_RESERVAS.porId(reservationId ?? ''), reservaComCondutoresSchema, {
        signal,
      }),
    enabled: reservationId !== null,
  })
}

/**
 * A frota inteira numa página só, para os selects de veículo desta fase (o
 * diálogo de multa, o de sinistro e o filtro da lista de sinistros).
 *
 * `incluirBaixados: true` de propósito: a notificação chega semanas depois da
 * infração, e o carro pode já ter saído da frota — a multa dele continua
 * chegando no CNPJ da locadora. No teto do servidor, como `useTabelasAtivas`:
 * se a frota passar de `TAMANHO_MAXIMO_DE_PAGINA`, o select precisa virar
 * campo de busca, e o corte silencioso aqui é o sintoma.
 */
const FILTRO_DA_FROTA_PARA_SELECAO: FiltrosDeVeiculos = {
  pagina: 1,
  tamanho: TAMANHO_MAXIMO_DE_PAGINA,
  busca: '',
  categoryId: '',
  locationId: '',
  status: '',
  blindagemVencendoEmDias: '',
  incluirBaixados: true,
}

export function useFrotaParaSelecao(): UseQueryResult<PaginaDeVeiculos> {
  return useVeiculos(FILTRO_DA_FROTA_PARA_SELECAO)
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

export function useRegistrarMulta(): UseMutationResult<Multa, Error, CorpoDeMulta> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeMulta) => apiPost(ROTAS_DE_MULTAS.lista, multaSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_MULTAS] })
    },
  })
}

export interface IndicacaoDeCondutor {
  readonly id: string
  /** Um condutor DA reserva vinculada — a API recusa qualquer outro. */
  readonly condutorId: string
}

export function useIndicarCondutor(): UseMutationResult<Multa, Error, IndicacaoDeCondutor> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, condutorId }: IndicacaoDeCondutor) =>
      apiPost(ROTAS_DE_MULTAS.indicarCondutor(id), multaSchema, { condutorId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_MULTAS] })
    },
  })
}

export interface AtualizacaoDeStatusDaMulta {
  readonly id: string
  readonly status: StatusDeMultaAtualizavel
}

export function useAtualizarStatusDaMulta(): UseMutationResult<
  Multa,
  Error,
  AtualizacaoDeStatusDaMulta
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: AtualizacaoDeStatusDaMulta) =>
      apiPatch(ROTAS_DE_MULTAS.porId(id), multaSchema, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_MULTAS] })
    },
  })
}

const cobrancaDeMultaSchema = z.object({
  multa: multaSchema,
  destino: z.enum(['CAUCAO', 'CARTAO']),
  valorDaMulta: z.string(),
  taxaAdministrativa: z.string(),
  total: z.string(),
  cobrancaEm: z.string().nullable(),
})

export type CobrancaDeMulta = z.infer<typeof cobrancaDeMultaSchema>

/**
 * "Cobrar do cliente" — a multa que chegou DEPOIS do fechamento, pela política
 * congelada na reserva: desconta da caução ainda retida, ou agenda no cartão
 * guardado no provedor (com aviso ao cliente). Sem caminho, a API responde 409
 * dizendo o que fazer.
 */
export function useCobrarMultaDoCliente(): UseMutationResult<CobrancaDeMulta, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiPost(ROTAS_DE_MULTAS.cobrarDoCliente(id), cobrancaDeMultaSchema, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_MULTAS] })
    },
  })
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/** O limiar do alerta de prazo — o mesmo do atalho "vencendo em 7 dias". */
export const DIAS_DE_ALERTA_DO_PRAZO = 7

/**
 * Os status em que a indicação AINDA corre contra o prazo. Depois de indicado
 * (ou cobrada, contestada, cancelada), o prazo virou história — pintar de
 * vermelho uma multa já resolvida encheria a lista de alertas que ninguém
 * precisa atender, e o vermelho perderia o significado onde importa.
 */
const STATUS_COM_INDICACAO_PENDENTE = [
  'RECEBIDA',
  'VINCULADA',
] as const satisfies readonly StatusDeMulta[]

export function indicacaoPendente(multa: Multa): boolean {
  return (STATUS_COM_INDICACAO_PENDENTE as readonly StatusDeMulta[]).includes(multa.status)
}

/**
 * `AAAA-MM-DD` de daqui a N dias, no fuso da OPERAÇÃO — é o valor que o atalho
 * "vencendo em 7 dias" põe no filtro `prazoAte`. `localDayKey` e não
 * `toISOString().slice(0, 10)` direto: às 22h de São Paulo o dia UTC já virou,
 * e o atalho pegaria um dia a mais.
 */
export function dataDaquiADias(dias: number): string {
  return localDayKey(new Date(Date.now() + dias * 86_400_000))
}

/**
 * `#a1b2c3d4` — o começo do id da reserva, para a coluna de vínculo.
 *
 * A resposta de multas e sinistros traz só o `reservationId`, não o número
 * sequencial da reserva; buscar o detalhe linha a linha seria N+1 numa coluna
 * de referência. Oito caracteres bastam para colar na busca da tela de
 * reservas, e o id completo vai no `title`.
 */
export function referenciaDaReserva(reservationId: string): string {
  return `#${reservationId.slice(0, 8)}`
}
